import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, BadgeCheck, Quote, Sparkles } from "lucide-react";
import { HOTEL, RATING_BREAKDOWN, REVIEWS } from "@/data/site";
import api from "@/lib/api";

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />
    ))}
  </div>
);

const AiSummary = () => {
  const [summary, setSummary] = useState("");
  useEffect(() => {
    const cached = sessionStorage.getItem("vsi_review_summary");
    if (cached) { setSummary(cached); return; }
    api.post("/ai/review-summary", { reviews: REVIEWS.map((r) => ({ rating: r.rating, comment: r.comment })) })
      .then(({ data }) => { setSummary(data.summary); sessionStorage.setItem("vsi_review_summary", data.summary); })
      .catch(() => {});
  }, []);
  if (!summary) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="mt-6 rounded-2xl border border-primary/25 bg-primary/5 p-5 flex gap-3"
      data-testid="review-ai-summary"
    >
      <span className="w-9 h-9 shrink-0 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Sparkles className="h-5 w-5" /></span>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-primary">AI Summary of Guest Reviews</div>
        <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{summary}</p>
      </div>
    </motion.div>
  );
};

export const Reviews = () => (
  <section id="reviews" className="py-16 lg:py-24 bg-muted/40">
    <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
      <span className="text-sm font-semibold uppercase tracking-[0.15em] text-primary">Guest Voices</span>
      <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold">What Our Guests Say</h2>

      <AiSummary />

      <div className="mt-8 grid lg:grid-cols-[300px_1fr] gap-8">
        <div className="rounded-2xl bg-card border border-border p-6 self-start">
          <div className="flex items-end gap-3">
            <span className="font-serif text-6xl font-bold text-primary leading-none">{HOTEL.rating}</span>
            <div className="pb-1">
              <Stars n={4} />
              <p className="text-xs text-muted-foreground mt-1">{HOTEL.reviews} Google reviews</p>
            </div>
          </div>
          <div className="mt-5 space-y-2.5">
            {RATING_BREAKDOWN.map((r) => (
              <div key={r.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{r.label}</span>
                  <span className="font-mono font-medium">{r.value}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: `${(r.value / 5) * 100}%` }}
                    viewport={{ once: true }} transition={{ duration: 0.8 }} className="h-full bg-secondary rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {REVIEWS.map((rev, i) => (
            <motion.div key={rev.author}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl bg-card border border-border p-5 relative"
              data-testid={`review-card-${i}`}
            >
              <Quote className="absolute top-4 right-4 h-6 w-6 text-primary/15" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold">
                  {rev.author.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-medium flex items-center gap-1">{rev.author} <BadgeCheck className="h-3.5 w-3.5 text-secondary" /></div>
                  <div className="text-xs text-muted-foreground">{rev.date}</div>
                </div>
              </div>
              <div className="mt-3"><Stars n={rev.rating} /></div>
              <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">{rev.comment}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
